//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { router } from '@/routes';
import {
  AuthContext, type AuthContextValue, type AuthStatus
} from '@/contexts/AuthContext';
import { authService, configService, sessionActivity } from '@/services';
import { type UserProfile } from '@/services/apiTypes';
import { registerQueryAuthErrorHandler } from '@/services/queryClient';
import { useTheme } from '@/theme/ThemeProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { ERoutesRoot } from '@/routes/RoutesEnum';

interface AuthProviderProps {
  children: React.ReactNode;
}

// Owns the reactive auth state and exposes the auth methods.  Sits above
// AppBootstrap so the startup orchestration there can set auth state through
// useAuth, while auth consumers stay isolated from the much more frequent
// dataset/query/config churn elsewhere.
const AuthProvider = ({ children }: AuthProviderProps) => {
  const handleError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const { handleLogout, setThemeKey } = useTheme();

  const [activeUser, setActiveUser] = useState<string | null>(null);
  const [activeUserAuthMethod, setActiveUserAuthMethod] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // authStatus is 'pending' until the startup session restore settles
  // (AppBootstrap calls markAuthResolved), then derives from activeUser.  This
  // is the reactive replacement for the ApiClient core's non-reactive userLoaded
  // flag: login/logout flip activeUser, so the route gate re-renders on its own.
  const [authResolved, setAuthResolved] = useState(false);
  const markAuthResolved = useCallback(() => setAuthResolved(true), []);
  const authStatus = useMemo<AuthStatus>(() => {
    if (!authResolved)
      return 'pending';
    return activeUser ? 'authenticated' : 'unauthenticated';
  }, [authResolved, activeUser]);

  // Update the theme before rendering whenever the profile's theme changes
  // (e.g. on login).
  useEffect(() => {
    if (userProfile?.theme)
      setThemeKey(userProfile.theme);
  }, [userProfile, setThemeKey]);

  // Route a 401 from any TanStack query (auto-fetch / focus-refetch) through
  // the same logout + redirect path useApiErrorHandler uses for direct calls.
  useEffect(() => {
    registerQueryAuthErrorHandler(() => {
      queryClient.clear();
      handleLogout();
      router.navigate(ERoutesRoot.Login);
    });
  }, [handleLogout, queryClient]);

  const logout = useCallback(async (broadcast = true) => {
    // Notify other tabs of this browser to log out too (unless this logout was
    // itself triggered by another tab's broadcast, which passes broadcast=false).
    if (broadcast)
      sessionActivity.broadcastLogout();
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      // Clear reactive auth state so the next session starts fresh: this
      // disables the xGT queries (enabled on activeUser) and lets DatasetProvider
      // reset the active-dataset selection so login re-restores from the profile.
      setActiveUser(null);
      setActiveUserAuthMethod('');
      setUserProfile(null);
      // Wipe the query cache so the next login refetches server state instead of
      // serving the previous session's cached (possibly stale) datasets/config.
      queryClient.clear();
      handleLogout();
    }
  }, [handleLogout, queryClient]);

  const restoreSession = useCallback(() =>
    handleError('restoreSession', () => authService.restoreSession()),
    [handleError]);

  const updateUserProfile = useCallback((profile: Partial<UserProfile>) =>
    handleError('updateUserProfile',
      () => configService.updateUserProfile(profile)),
    [handleError]);

  const value = useMemo((): AuthContextValue => ({
    activeUser, setActiveUser,
    activeUserAuthMethod, setActiveUserAuthMethod,
    userProfile, setUserProfile,
    authStatus, markAuthResolved,

    // Core-owned session state, non-reactive — snapshotted when this value
    // recomputes.
    xgtConnectionInfo: authService.getXgtConnectionInfo(),

    logout,
    restoreSession,
    isAuthenticated: authService.isAuthenticated,
    getSessionExpiredTime: authService.getSessionExpiredTime,
    setRestoreSessionEnabled: authService.setRestoreSessionEnabled,
    clearSessionExpired: authService.clearSessionExpired,
    getOidcAuthorizationUrl: authService.getOidcAuthorizationUrl,
    updateUserProfile,
  }), [activeUser, activeUserAuthMethod, userProfile, authStatus,
       markAuthResolved, logout, restoreSession, updateUserProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
